import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useAuth } from "../context/AuthContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Mail, Lock, LogIn, AlertCircle, Bell, ArrowLeft, Sparkles, MessageSquare } from 'lucide-react';

const IS_SAAS = process.env.REACT_APP_IS_SAAS === "true";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useTranslation('auth');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    
    try {
      const result = await login(email, password);
      if (result?.superadmin) {
        navigate('/admin', { replace: true });
      } else {
        const from = location.state?.from || "/home";
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error("Login error:", error);
      setError(t('login.loginError', { message: error.message }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Link
        to="/"
        className="fixed top-4 left-4 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        {t('login.backButton')}
      </Link>
      <div className="fixed top-4 right-4">
        <LanguageSwitcher />
      </div>
      <div className="max-w-md w-full p-6 space-y-8">
        {/* Logo ve Başlık */}
        <div className="text-center">
          <div className="w-full h-12 mb-6 flex justify-center">
            <div className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-2xl font-bold py-2 px-4 rounded-lg">
              Adisyon Takip
            </div>
          </div>
          <div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900">
              {t('login.pageTitle')}
            </h2>
          </div>
        </div>

        {/* Yeni Özellikler Duyurusu */}
        <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-l-4 border-blue-500 p-4 rounded-md shadow-md">
          <div className="flex flex-col space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-blue-700">{t('login.newFeaturesTitle')} <span className="text-xs font-normal text-blue-500">v2.0.2</span></h3>
              <Bell className="h-5 w-5 text-blue-500 animate-bounce" />
            </div>
            
            <div className="grid grid-cols-1 gap-3 pt-2">
              <div className="flex items-center space-x-2 bg-white p-2 rounded-md shadow-sm">
                <MessageSquare className="h-5 w-5 text-orange-500" />
                <span className="text-sm text-gray-700">{t('login.newFeature1')}</span>
              </div>

              <div className="flex items-center space-x-2 bg-white p-2 rounded-md shadow-sm">
                <Sparkles className="h-5 w-5 text-purple-500" />
                <span className="text-sm text-gray-700">{t('login.newFeature2')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Hata Mesajı */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Giriş Formu */}
        <form onSubmit={handleLogin} className="mt-8 space-y-6">
          <div className="space-y-4">
            {/* Email Alanı */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('login.emailLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md 
                    shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 
                    focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('login.emailPlaceholder')}
                />
              </div>
            </div>

            {/* Şifre Alanı */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('login.passwordLabel')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md 
                    shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 
                    focus:ring-blue-500 focus:border-transparent"
                  placeholder="••••••••"
                />
              </div>
            </div>
          </div>

          {/* Giriş Butonu */}
          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent 
                text-sm font-medium rounded-md text-white bg-gradient-to-r from-cyan-500 to-blue-500 
                hover:from-cyan-600 hover:to-blue-600 focus:outline-none focus:ring-2 
                focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300"
            >
              <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                <LogIn className="h-5 w-5" />
              </span>
              {t('login.loginButton')}
            </button>
          </div>
        </form>

        {/* Kayıt Ol Linki */}
        {IS_SAAS && (
          <div className="text-center">
            <p className="text-sm text-gray-500">
              {t('login.noAccountPrompt')}{' '}
              <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                {t('login.registerLink')}
              </Link>
            </p>
          </div>
        )}

        {/* Alt Bilgi */}
        <div className="mt-2">
          <p className="text-center text-sm text-gray-500">
            {t('login.copyright')}
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;